/**
 * RPC Client - Shared wrapper for Supabase RPC calls with caching and deduplication
 * 
 * Features:
 * - In-memory cache with TTL
 * - In-flight promise deduplication (prevents duplicate concurrent requests)
 * - Integration with SessionManager for session-based RPCs
 * - Circuit breaker per endpoint (prevents request storms)
 * - Rate limiter (global safety fuse)
 * - Exponential backoff with jitter for retries
 * - DEV-only loop detection
 * - BLACKLIST for volatile endpoints (no caching)
 * 
 * Usage:
 *   // No cache
 *   const result = await rpcNoCache('my_rpc', { arg: 'value' });
 *   
 *   // With cache
 *   const result = await rpcCached('my_rpc', { arg: 'value' }, { ttlMs: 30000, key: 'unique-key', reason: 'myFeature' });
 *   
 *   // With session, cache, and safety (circuit breaker + rate limiting)
 *   const result = await rpcWithSessionSafe('my_rpc', s => ({ p_session_token: s.sessionToken }), { ttlMs: 30000 });
 */

import { supabase } from '@/integrations/supabase/client';
import { SessionManager, type SessionInfo } from './sessionManager';
import { recordRequest } from '@/dev/loopGuard';
import { DEBUG_PROGRESS, diagCacheEvent } from '@/dev/debugProgressDiag';

// ============= Cache Storage =============
interface CacheEntry {
  value: unknown;
  at: number;
}

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<unknown>>();
const DEFAULT_TTL_MS = 60_000;

// ============= BLACKLIST: Endpoints that should NEVER be cached =============
// These are volatile/real-time endpoints where caching would freeze values
const CACHE_BLACKLIST = new Set([
  'get_freeworld_balance',
  'get_or_create_financial_status_with_session',
]);

// Table names that should not be cached (for direct queries)
const TABLE_CACHE_BLACKLIST = new Set([
  'player_financial_status',
]);

// ============= Circuit Breaker =============
interface CircuitState {
  failures: number;
  lastFailure: number;
  openedAt: number;
  state: 'closed' | 'open' | 'half-open';
}

const circuitBreakers = new Map<string, CircuitState>();
const CIRCUIT_FAILURE_THRESHOLD = 3;
const CIRCUIT_RESET_MS = 60_000; // 60 seconds cooldown when open
const CIRCUIT_MONITOR_WINDOW_MS = 30_000; // 30 second window for failure counting

// ============= Rate Limiter (Global Safety Fuse) =============
interface RateLimitEntry {
  count: number;
  windowStart: number;
  blockedUntil: number;
}

const rateLimiter = new Map<string, RateLimitEntry>();
const RATE_LIMIT_MAX_CALLS = 20;
const RATE_LIMIT_WINDOW_MS = 10_000; // 10 seconds
const RATE_LIMIT_BLOCK_MS = 60_000; // Block for 60 seconds

// ============= Retry Configuration =============
const MAX_RETRIES = 2; // 3 attempts total
const BACKOFF_BASE_MS = 300;
const BACKOFF_MAX_MS = 2000;

// ============= Helper Functions =============

/**
 * Check if an RPC name is blacklisted from caching
 */
function isBlacklisted(name: string): boolean {
  return CACHE_BLACKLIST.has(name);
}

/**
 * Check if a table name is blacklisted from caching
 */
function isTableBlacklisted(tableName: string): boolean {
  return TABLE_CACHE_BLACKLIST.has(tableName);
}

/**
 * Generate a stable cache key from RPC name and args
 */
function generateCacheKey(name: string, args: Record<string, unknown>, customKey?: string): string {
  if (customKey) {
    return `${name}:${customKey}`;
  }
  const sortedArgs = Object.keys(args)
    .sort()
    .map(k => `${k}=${JSON.stringify(args[k])}`)
    .join('&');
  return `${name}:${sortedArgs}`;
}

function isCacheValid(entry: CacheEntry | undefined, ttlMs: number): boolean {
  if (!entry) return false;
  if (ttlMs <= 0) return false; // TTL 0 means no caching
  return Date.now() - entry.at < ttlMs;
}

/**
 * Check if error is a CORS or network error (not retryable)
 */
function isCorsOrNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    return msg.includes('failed to fetch') || msg.includes('network') || msg.includes('cors');
  }
  return false;
}

/**
 * Check if error is a permanent failure (4xx, should not retry)
 */
function isPermanentError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    const code = err.code || err.status;
    const message = String(err.message || '').toLowerCase();
    
    // 4xx errors (except 429 rate limit)
    if (typeof code === 'number' && code >= 400 && code < 500 && code !== 429) {
      return true;
    }
    
    // String codes from PostgREST
    if (code === 'PGRST301' || code === 'PGRST302' || code === '42501') {
      return true; // Permission denied, not found
    }
    
    // Specific error messages
    if (message.includes('invalid_session') || message.includes('unauthorized') || 
        message.includes('forbidden') || message.includes('not found')) {
      return true;
    }
  }
  return false;
}

/**
 * Calculate backoff with jitter
 */
function getBackoffMs(attempt: number): number {
  const base = BACKOFF_BASE_MS * Math.pow(3, attempt); // 300, 900, 2700
  const jitter = Math.random() * 100;
  return Math.min(base + jitter, BACKOFF_MAX_MS);
}

// ============= Circuit Breaker Functions =============

function getCircuitKey(rpcName: string, playerId?: string): string {
  return playerId ? `${rpcName}:${playerId.slice(-8)}` : rpcName;
}

function getCircuitState(key: string): CircuitState {
  let state = circuitBreakers.get(key);
  if (!state) {
    state = { failures: 0, lastFailure: 0, openedAt: 0, state: 'closed' };
    circuitBreakers.set(key, state);
  }
  
  const now = Date.now();
  
  // Check if we should transition from open to half-open
  if (state.state === 'open' && now - state.openedAt > CIRCUIT_RESET_MS) {
    state.state = 'half-open';
    console.debug('[rpc] circuit-half-open', key);
  }
  
  // Reset failure count if outside monitoring window
  if (state.state === 'closed' && now - state.lastFailure > CIRCUIT_MONITOR_WINDOW_MS) {
    state.failures = 0;
  }
  
  return state;
}

function recordCircuitFailure(key: string): boolean {
  const state = getCircuitState(key);
  const now = Date.now();
  
  state.failures++;
  state.lastFailure = now;
  
  if (state.failures >= CIRCUIT_FAILURE_THRESHOLD) {
    state.state = 'open';
    state.openedAt = now;
    console.error('[rpc] circuit-open', { key, failures: state.failures, cooldownMs: CIRCUIT_RESET_MS });
    return true; // Circuit just opened
  }
  
  return false;
}

function recordCircuitSuccess(key: string): void {
  const state = getCircuitState(key);
  if (state.state === 'half-open') {
    state.state = 'closed';
    state.failures = 0;
    console.debug('[rpc] circuit-closed', key);
  }
}

function isCircuitOpen(key: string): boolean {
  const state = getCircuitState(key);
  return state.state === 'open';
}

// ============= Rate Limiter Functions =============

function checkRateLimit(rpcName: string): { allowed: boolean; reason?: string } {
  const now = Date.now();
  let entry = rateLimiter.get(rpcName);
  
  if (!entry) {
    entry = { count: 0, windowStart: now, blockedUntil: 0 };
    rateLimiter.set(rpcName, entry);
  }
  
  // Check if currently blocked
  if (entry.blockedUntil > now) {
    const remainingMs = entry.blockedUntil - now;
    console.error('[rpc] fuse-tripped', { rpcName, remainingMs, reason: 'rate limit exceeded' });
    return { allowed: false, reason: `Rate limited for ${Math.ceil(remainingMs / 1000)}s` };
  }
  
  // Reset window if expired
  if (now - entry.windowStart > RATE_LIMIT_WINDOW_MS) {
    entry.count = 0;
    entry.windowStart = now;
  }
  
  entry.count++;
  
  // Check if limit exceeded
  if (entry.count > RATE_LIMIT_MAX_CALLS) {
    entry.blockedUntil = now + RATE_LIMIT_BLOCK_MS;
    console.error('[rpc] fuse-tripped', { rpcName, count: entry.count, blockMs: RATE_LIMIT_BLOCK_MS });
    return { allowed: false, reason: 'Too many requests, blocked for 60s' };
  }
  
  return { allowed: true };
}

// ============= Cache Management =============

export function invalidateCache(key: string): void {
  cache.delete(key);
  console.debug('[rpc] cache invalidated', key);
}

export function invalidateCacheByRpc(rpcName: string): void {
  const keysToDelete: string[] = [];
  cache.forEach((_, key) => {
    if (key.startsWith(`${rpcName}:`)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach(k => cache.delete(k));
  if (keysToDelete.length > 0) {
    console.debug('[rpc] cache invalidated for', rpcName, keysToDelete.length, 'entries');
  }
}

export function clearRpcCache(): void {
  cache.clear();
  console.debug('[rpc] cache cleared');
}

export function resetCircuitBreaker(rpcName: string): void {
  circuitBreakers.forEach((_, key) => {
    if (key.startsWith(rpcName)) {
      circuitBreakers.delete(key);
    }
  });
  console.debug('[rpc] circuit breaker reset for', rpcName);
}

interface RpcOptions {
  ttlMs?: number;
  key?: string;
  reason?: string;
  /** Force bypass cache even for cached endpoints */
  bypassCache?: boolean;
}

interface RpcSafeOptions extends RpcOptions {
  /** Maximum retry attempts (default: 2, meaning 3 total attempts) */
  maxRetries?: number;
  /** Minimum interval between calls in ms (default: 0) */
  minIntervalMs?: number;
  /** Player ID for circuit breaker keying */
  playerId?: string;
}

/**
 * Execute RPC without caching
 */
export async function rpcNoCache<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  options: { reason?: string } = {}
): Promise<{ data: T | null; error: unknown }> {
  const startTime = Date.now();
  const reason = options.reason || 'direct';
  
  // DEV: Record for loop detection
  recordRequest('POST', `/rest/v1/rpc/${name}`);
  
  try {
    // Use 'as any' to allow dynamic RPC names
    const result = await (supabase.rpc as any)(name, args);
    console.debug('[rpc] fetched', name, `${Date.now() - startTime}ms`, reason);
    return result as { data: T | null; error: unknown };
  } catch (err) {
    console.error('[rpc] error', name, err);
    return { data: null, error: err };
  }
}

/**
 * Execute RPC with caching and in-flight deduplication
 * NOTE: Blacklisted RPCs will have TTL forced to 0 (in-flight dedup only)
 */
export async function rpcCached<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  options: RpcOptions = {}
): Promise<{ data: T | null; error: unknown }> {
  // Force TTL to 0 for blacklisted endpoints (in-flight dedup still works)
  const effectiveTtl = isBlacklisted(name) ? 0 : (options.ttlMs ?? DEFAULT_TTL_MS);
  const { key: customKey, reason = 'cached', bypassCache = false } = options;
  const cacheKey = generateCacheKey(name, args, customKey);
  
  // Check cache first (if not blacklisted and not bypassed)
  if (!bypassCache && effectiveTtl > 0) {
    const cached = cache.get(cacheKey);
    if (isCacheValid(cached, effectiveTtl)) {
      console.debug('[rpc] cache hit', name, cacheKey, reason);
      return { data: cached!.value as T, error: null };
    }
  }
  
  // Check if request is already in flight (always applies, even with TTL 0)
  const existing = inFlight.get(cacheKey);
  if (existing) {
    console.debug('[rpc] in-flight reused', name, cacheKey, reason);
    return existing as Promise<{ data: T | null; error: unknown }>;
  }
  
  // DEV: Record for loop detection
  recordRequest('POST', `/rest/v1/rpc/${name}`);
  
  // Create new request
  const startTime = Date.now();
  const promise = (async () => {
    try {
      // Use 'as any' to allow dynamic RPC names
      const result = await (supabase.rpc as any)(name, args);
      
      // Only cache if TTL > 0 and no error
      if (!result.error && result.data !== null && effectiveTtl > 0) {
        cache.set(cacheKey, { value: result.data, at: Date.now() });
      }
      
      console.debug('[rpc] fetched', name, `${Date.now() - startTime}ms`, reason);
      return result;
    } catch (err) {
      console.error('[rpc] error', name, err);
      return { data: null, error: err };
    } finally {
      // Clean up in-flight
      inFlight.delete(cacheKey);
    }
  })();
  
  // Store in-flight promise
  inFlight.set(cacheKey, promise);
  
  return promise as Promise<{ data: T | null; error: unknown }>;
}

interface RpcWithSessionOptions extends RpcOptions {
  /** If true, use cache. If false, no caching. Default: false for blacklisted, true for others */
  cache?: boolean;
}

/**
 * Execute RPC with SessionManager integration and optional caching
 * Automatically handles session retry on invalid_session errors
 * NOTE: Blacklisted RPCs will have caching disabled by default
 */
export async function rpcWithSession<T = unknown>(
  name: string,
  argsBuilder: (session: SessionInfo) => Record<string, unknown>,
  options: RpcWithSessionOptions = {}
): Promise<{ data: T | null; error: unknown }> {
  // Default: cache unless blacklisted
  const shouldCache = options.cache ?? !isBlacklisted(name);
  const effectiveTtl = isBlacklisted(name) ? 0 : (options.ttlMs ?? DEFAULT_TTL_MS);
  const { key: customKey, reason = 'withSession', bypassCache = false } = options;
  
  // Get session first to build cache key
  const session = await SessionManager.getSession();
  if (!session) {
    console.debug('[rpc] no session for', name);
    return { data: null, error: new Error('No valid session') };
  }
  
  const args = argsBuilder(session);
  const cacheKey = generateCacheKey(name, { ...args, _playerId: session.playerId }, customKey);
  
  if (shouldCache && !bypassCache && effectiveTtl > 0) {
    // Check cache first
    const cached = cache.get(cacheKey);
    if (isCacheValid(cached, effectiveTtl)) {
      // DIAG: Log cache hit with details
      if (DEBUG_PROGRESS && name.includes('quiz_progress')) {
        diagCacheEvent('HIT', name, cacheKey, { 
          ageMs: Date.now() - cached!.at, 
          ttlMs: effectiveTtl 
        });
      }
      console.debug('[rpc] cache hit', name, cacheKey, reason);
      return { data: cached!.value as T, error: null };
    } else if (cached) {
      // Cache exists but expired
      if (DEBUG_PROGRESS && name.includes('quiz_progress')) {
        diagCacheEvent('EXPIRED', name, cacheKey, { 
          ageMs: Date.now() - cached.at, 
          ttlMs: effectiveTtl 
        });
      }
    }
  }
  
  // DIAG: Log cache miss
  if (DEBUG_PROGRESS && name.includes('quiz_progress')) {
    diagCacheEvent('MISS', name, cacheKey, { ttlMs: effectiveTtl });
  }
  
  // Check if request is already in flight (always applies)
  const existing = inFlight.get(cacheKey);
  if (existing) {
    console.debug('[rpc] in-flight reused', name, cacheKey, reason);
    return existing as Promise<{ data: T | null; error: unknown }>;
  }
  
  // DEV: Record for loop detection
  recordRequest('POST', `/rest/v1/rpc/${name}`);
  
  // Create new request with session retry
  const startTime = Date.now();
  const promise = (async () => {
    try {
      const result = await SessionManager.withSessionRetry(async (s) => {
        const freshArgs = argsBuilder(s);
        // Use 'as any' to allow dynamic RPC names
        return (supabase.rpc as any)(name, freshArgs);
      });
      
      // Only cache if enabled and no error
      if (!result.error && result.data !== null && shouldCache && effectiveTtl > 0) {
        cache.set(cacheKey, { value: result.data, at: Date.now() });
      }
      
      console.debug('[rpc] fetched', name, `${Date.now() - startTime}ms`, reason);
      return result as { data: T | null; error: unknown };
    } catch (err) {
      console.error('[rpc] error', name, err);
      return { data: null, error: err };
    } finally {
      // Clean up in-flight
      inFlight.delete(cacheKey);
    }
  })();
  
  // Store in-flight promise
  inFlight.set(cacheKey, promise);
  
  return promise;
}

/**
 * Shorthand for rpcWithSession with caching enabled
 * NOTE: Blacklisted RPCs will still have TTL 0
 */
export async function rpcWithSessionCached<T = unknown>(
  name: string,
  argsBuilder: (session: SessionInfo) => Record<string, unknown>,
  options: RpcOptions = {}
): Promise<{ data: T | null; error: unknown }> {
  return rpcWithSession<T>(name, argsBuilder, { ...options, cache: true });
}

/**
 * Execute RPC with full safety: circuit breaker + rate limiting + retries + backoff + caching
 * This is the RECOMMENDED function for critical RPCs that must not cause request storms.
 * NOTE: Blacklisted RPCs will have caching disabled (but still get circuit breaker + rate limiting)
 */
export async function rpcWithSessionSafe<T = unknown>(
  name: string,
  argsBuilder: (session: SessionInfo) => Record<string, unknown>,
  options: RpcSafeOptions = {}
): Promise<{ data: T | null; error: unknown; fromCache?: boolean; circuitOpen?: boolean }> {
  // Force TTL to 0 for blacklisted endpoints
  const effectiveTtl = isBlacklisted(name) ? 0 : (options.ttlMs ?? DEFAULT_TTL_MS);
  const { 
    key: customKey, 
    reason = 'safe',
    maxRetries = MAX_RETRIES,
    playerId,
    bypassCache = false
  } = options;
  
  const circuitKey = getCircuitKey(name, playerId);
  
  // 1. Check rate limit first (global safety fuse)
  const rateCheck = checkRateLimit(name);
  if (!rateCheck.allowed) {
    // Return stale cache if available
    const session = await SessionManager.getSession();
    if (session) {
      const args = argsBuilder(session);
      const cacheKey = generateCacheKey(name, { ...args, _playerId: session.playerId }, customKey);
      const cached = cache.get(cacheKey);
      if (cached) {
        console.debug('[rpc] rate-limited, returning stale cache', name);
        return { data: cached.value as T, error: null, fromCache: true };
      }
    }
    return { data: null, error: new Error(rateCheck.reason || 'Rate limited') };
  }
  
  // 2. Check circuit breaker
  if (isCircuitOpen(circuitKey)) {
    console.debug('[rpc] circuit-skip', { name, circuitKey });
    // Return stale cache if available
    const session = await SessionManager.getSession();
    if (session) {
      const args = argsBuilder(session);
      const cacheKey = generateCacheKey(name, { ...args, _playerId: session.playerId }, customKey);
      const cached = cache.get(cacheKey);
      if (cached) {
        console.debug('[rpc] circuit-open, returning stale cache', name);
        return { data: cached.value as T, error: null, fromCache: true, circuitOpen: true };
      }
    }
    return { data: null, error: new Error('Circuit breaker open'), circuitOpen: true };
  }
  
  // 3. Get session
  const session = await SessionManager.getSession();
  if (!session) {
    console.debug('[rpc] no session for', name);
    return { data: null, error: new Error('No valid session') };
  }
  
  const args = argsBuilder(session);
  const cacheKey = generateCacheKey(name, { ...args, _playerId: session.playerId }, customKey);
  
  // 4. Check cache (if not blacklisted and not bypassed)
  if (!bypassCache && effectiveTtl > 0) {
    const cached = cache.get(cacheKey);
    if (isCacheValid(cached, effectiveTtl)) {
      console.debug('[rpc] cache hit', name, cacheKey, reason);
      return { data: cached!.value as T, error: null, fromCache: true };
    }
  }
  
  // 5. Check if request is already in flight (always applies)
  const existing = inFlight.get(cacheKey);
  if (existing) {
    console.debug('[rpc] in-flight reused', name, cacheKey, reason);
    return existing as Promise<{ data: T | null; error: unknown }>;
  }
  
  // 6. Execute with retries
  const startTime = Date.now();
  const promise = (async () => {
    let lastError: unknown = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Log attempt
        if (attempt > 0) {
          const backoffMs = getBackoffMs(attempt - 1);
          console.debug('[rpc] retrying', { name, attempt: attempt + 1, backoffMs });
          await new Promise(resolve => setTimeout(resolve, backoffMs));
        } else {
          console.debug('[rpc] attempt', { name, attempt: 1 });
        }
        
        // DEV: Record for loop detection
        recordRequest('POST', `/rest/v1/rpc/${name}`);
        
        // Get fresh session for retry
        const freshSession = attempt > 0 ? await SessionManager.getSession() : session;
        if (!freshSession) {
          throw new Error('No valid session');
        }
        
        const freshArgs = argsBuilder(freshSession);
        const result = await (supabase.rpc as any)(name, freshArgs);
        
        if (result.error) {
          throw result.error;
        }
        
        // Success! Record and cache (if TTL > 0)
        recordCircuitSuccess(circuitKey);
        
        if (result.data !== null && effectiveTtl > 0) {
          cache.set(cacheKey, { value: result.data, at: Date.now() });
        }
        
        console.debug('[rpc] fetched', name, `${Date.now() - startTime}ms`, reason);
        return { data: result.data as T, error: null };
        
      } catch (error) {
        lastError = error;
        
        // Check if we should NOT retry
        if (isCorsOrNetworkError(error)) {
          console.debug('[rpc] no-retry', { name, reason: 'CORS/network error' });
          recordCircuitFailure(circuitKey);
          break; // Don't retry CORS errors
        }
        
        if (isPermanentError(error)) {
          console.debug('[rpc] no-retry', { name, reason: 'permanent error', error });
          break; // Don't retry permanent errors
        }
        
        // Record failure for circuit breaker
        if (attempt === maxRetries) {
          recordCircuitFailure(circuitKey);
        }
      }
    }
    
    // All retries exhausted
    console.error('[rpc] error after retries', name, lastError);
    
    // Return stale cache as fallback if available
    const staleCache = cache.get(cacheKey);
    if (staleCache) {
      console.debug('[rpc] returning stale cache after error', name);
      return { data: staleCache.value as T, error: lastError, fromCache: true };
    }
    
    return { data: null, error: lastError };
  })();
  
  // Store in-flight promise
  inFlight.set(cacheKey, promise);
  
  // Clean up on completion
  promise.finally(() => {
    inFlight.delete(cacheKey);
  });
  
  return promise;
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats(): { 
  size: number; 
  inFlightCount: number;
  circuitBreakers: Record<string, string>;
  rateLimiters: Record<string, { count: number; blocked: boolean }>;
  blacklist: string[];
} {
  const circuits: Record<string, string> = {};
  circuitBreakers.forEach((state, key) => {
    circuits[key] = state.state;
  });
  
  const limits: Record<string, { count: number; blocked: boolean }> = {};
  const now = Date.now();
  rateLimiter.forEach((entry, key) => {
    limits[key] = { count: entry.count, blocked: entry.blockedUntil > now };
  });
  
  return {
    size: cache.size,
    inFlightCount: inFlight.size,
    circuitBreakers: circuits,
    rateLimiters: limits,
    blacklist: Array.from(CACHE_BLACKLIST)
  };
}

/**
 * Check if an RPC is blacklisted (for external use)
 */
export function isRpcBlacklisted(name: string): boolean {
  return isBlacklisted(name);
}
